// Diff util para preview del Deploy. Compara el config actual del bot
// (BotDefinition serializado en `bots.config`) contra el config de un
// deployment previo y resume qué cambió.
//
// Diseño: aplanamos ambos objetos a paths dot/bracket y clasificamos cada
// path en `added` / `removed` / `changed`. La comparación de valores usa
// `JSON.stringify` — suficiente para shapes serializables (no hay funciones
// ni Dates en BotDefinition).

export interface ConfigDiff {
  added: string[];
  removed: string[];
  changed: string[];
}

const MAX_DEPTH = 10;

function flatten(value: unknown, prefix = '', out = new Map<string, string>(), depth = 0): void {
  if (depth > MAX_DEPTH) return;
  if (value == null || typeof value !== 'object') {
    out.set(prefix || '<root>', JSON.stringify(value));
    return;
  }
  if (Array.isArray(value)) {
    // Comparamos el array completo como una unidad — diff item-por-item de
    // arrays mixtos ruidoso para el usuario. Si el array cambió, mostramos
    // el path al array sin recursar adentro.
    out.set(prefix, JSON.stringify(value));
    return;
  }
  const obj = value as Record<string, unknown>;
  for (const [k, v] of Object.entries(obj)) {
    if (k === '_source') continue;
    const path = prefix ? `${prefix}.${k}` : k;
    flatten(v, path, out, depth + 1);
  }
}

export function diffConfig(prev: unknown, next: unknown): ConfigDiff {
  const prevMap = new Map<string, string>();
  const nextMap = new Map<string, string>();
  flatten(prev, '', prevMap);
  flatten(next, '', nextMap);

  const added: string[] = [];
  const removed: string[] = [];
  const changed: string[] = [];

  for (const [k, v] of nextMap) {
    const prevV = prevMap.get(k);
    if (prevV === undefined) added.push(k);
    else if (prevV !== v) changed.push(k);
  }
  for (const k of prevMap.keys()) {
    if (!nextMap.has(k)) removed.push(k);
  }

  return { added, removed, changed };
}

export function isEmpty(diff: ConfigDiff): boolean {
  return diff.added.length === 0 && diff.removed.length === 0 && diff.changed.length === 0;
}
